import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
    LayoutDashboard, Users, Receipt, Wallet, UserCog, LogOut, Home,
    PieChart as PieIcon, Sparkles, MessageSquare, PhoneCall, ShoppingBag,
    ArrowRightLeft, Play, RefreshCcw, Send, Mail, TrendingUp,
    Menu, X,
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { PlansTab, FollowupsTab, MemberManagerModal, TrainerRosterTab } from "../components/admin/AdminExt";
import { BiometricTab, HolidaysTab, CouponsTab, CouponValidator, OtpAuditTab } from "../components/admin/AdminPhaseB";
import { CreditCard, ClipboardList, UsersRound, Download, Fingerprint, Calendar, Ticket, ShieldCheck } from "lucide-react";

const money = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const Card = ({ children, className = "" }) => <div className={`card-noir p-6 lg:p-8 ${className}`}>{children}</div>;
const todayStr = () => new Date().toISOString().slice(0, 10);
const monthKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

// ---------- Overview ----------
const Overview = () => {
    const [o, setO] = useState(null);
    const [members, setMembers] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const load = useCallback(async () => {
        const [a, b, c] = await Promise.all([api.get("/admin/overview"), api.get("/admin/members"), api.get("/admin/expenses")]);
        setO(a.data); setMembers(b.data); setExpenses(c.data);
    }, []);
    useEffect(() => { load(); }, [load]);
    const seed = async () => { await api.post("/admin/seed"); toast.success("Sample data ready"); load(); };
    if (!o) return <div className="text-sand">Loading…</div>;
    const expByCat = Object.entries(expenses.reduce((a, e) => { a[e.category] = (a[e.category] || 0) + e.amount; return a; }, {}))
        .map(([name, value], i) => ({ name, value, color: ["#D4AF37", "#DC143C", "#8B0000", "#AA7C11", "#FFF3C4", "#2B2634"][i % 6] }));
    return (
        <div className="grid lg:grid-cols-4 gap-5">
            {[
                { label: "Members", v: o.members, sub: `${o.active_members} active`, ic: Users },
                { label: "Today's check-ins", v: o.todays_checkins, sub: "Present today", ic: Sparkles },
                { label: "Revenue (paid)", v: money(o.revenue), sub: money(o.pending) + " pending", ic: Receipt },
                { label: "Expenses (total)", v: money(o.total_expense), sub: "All time", ic: Wallet },
                { label: "New leads", v: o.new_leads, sub: "Unread", ic: MessageSquare },
                { label: "Codex sold", v: o.codex_paid, sub: money(o.codex_revenue), ic: ShoppingBag },
            ].map((k) => (
                <Card key={k.label}>
                    <div className="flex items-center justify-between"><div className="font-mono-label">{k.label}</div><k.ic className="w-4 h-4 text-gold" /></div>
                    <div className="font-display text-3xl gold-text mt-3">{k.v}</div>
                    <div className="text-xs text-sand/70 mt-1">{k.sub}</div>
                </Card>
            ))}
            <Card className="lg:col-span-2">
                <div className="flex items-center justify-between"><div className="font-mono-label">Expense split</div><PieIcon className="w-4 h-4 text-gold" /></div>
                <div className="h-64 mt-2">
                    <ResponsiveContainer><PieChart>
                        <Pie data={expByCat.length ? expByCat : [{ name: "None", value: 1, color: "#2B2634" }]} dataKey="value" innerRadius={60} outerRadius={90} stroke="none">
                            {(expByCat.length ? expByCat : [{ color: "#2B2634" }]).map((d, i) => <Cell key={d.name || i} fill={d.color} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#121016", border: "1px solid #D4AF37", color: "#FAF5E9" }} />
                    </PieChart></ResponsiveContainer>
                </div>
            </Card>
            <Card className="lg:col-span-2">
                <div className="font-mono-label">Recent members</div>
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                    {members.slice(0, 6).map((m) => (
                        <div key={m.id} className="flex justify-between border border-gold/15 rounded-xl p-3">
                            <div><div className="text-ivory">{m.name}</div><div className="text-xs text-sand/70">{m.phone}</div></div>
                            <div className="text-gold text-xs">{m.plan}</div>
                        </div>
                    ))}
                    {!members.length && <button onClick={seed} className="btn-gold" data-testid="seed-btn">Seed sample data</button>}
                </div>
            </Card>
        </div>
    );
};

// ---------- Attendance (with monthly rollup) ----------
const AttendanceTab = () => {
    const [rows, setRows] = useState([]); const [members, setMembers] = useState([]);
    const [mid, setMid] = useState(""); const [status, setStatus] = useState("Present");
    const today = todayStr();
    const load = useCallback(async () => {
        const [a, m] = await Promise.all([api.get("/admin/attendance", { params: { date: today } }), api.get("/admin/members")]);
        setRows(a.data); setMembers(m.data);
    }, [today]);
    useEffect(() => { load(); }, [load]);
    const add = async () => {
        const m = members.find((x) => x.id === mid); if (!m) return toast.error("Pick a member");
        await api.post("/admin/attendance", { member_id: m.id, member_name: m.name, date: today, status });
        toast.success("Check-in logged"); load();
    };

    // Rollup state
    const [rollupMid, setRollupMid] = useState("");
    const [rollupMonth, setRollupMonth] = useState(monthKey());
    const [rollup, setRollup] = useState(null);
    const loadRollup = useCallback(async () => {
        if (!rollupMid) return;
        const [y, m] = rollupMonth.split("-").map(Number);
        const { data } = await api.get(`/admin/attendance/member/${rollupMid}`, { params: { year: y, month: m } });
        setRollup(data);
    }, [rollupMid, rollupMonth]);
    useEffect(() => { loadRollup(); }, [loadRollup]);

    const daysInMonth = (ym) => {
        const [y, m] = ym.split("-").map(Number);
        return new Date(y, m, 0).getDate();
    };
    const startDow = (ym) => {
        const [y, m] = ym.split("-").map(Number);
        return new Date(y, m - 1, 1).getDay();
    };
    const statusOf = (dateStr) => rollup?.rows?.find((r) => r.date === dateStr)?.status;

    const cellColor = (s) => s === "Present" ? "bg-gold/30 border-gold text-gold"
        : s === "Late" ? "bg-sand/25 border-sand text-sand"
        : s === "Absent" ? "bg-crimson/25 border-crimson text-crimson"
        : "border-gold/15 text-sand/60";

    return (
        <div className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
                <Card>
                    <div className="font-mono-label">Log check-in</div>
                    <Label className="mt-4 block">Member</Label>
                    <Select value={mid} onValueChange={setMid}><SelectTrigger data-testid="att-member-trigger"><SelectValue placeholder="Choose" /></SelectTrigger>
                        <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>
                    <Label className="mt-3 block">Status</Label>
                    <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{["Present", "Absent", "Late"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    <Button onClick={add} className="btn-gold w-full mt-5" data-testid="att-save-btn">Log</Button>
                </Card>
                <Card className="lg:col-span-2">
                    <div className="font-mono-label">Today · {today}</div>
                    <Table className="mt-3"><TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Status</TableHead><TableHead>Time</TableHead></TableRow></TableHeader>
                        <TableBody>{rows.map((r) => (<TableRow key={r.id} data-testid={`att-row-${r.id}`}>
                            <TableCell className="text-ivory">{r.member_name}</TableCell><TableCell className="text-gold">{r.status}</TableCell>
                            <TableCell className="text-sand/70 text-xs">{new Date(r.check_in).toLocaleTimeString()}</TableCell>
                        </TableRow>))}{!rows.length && <TableRow><TableCell colSpan={3} className="text-sand/70 text-center">No check-ins yet</TableCell></TableRow>}</TableBody>
                    </Table>
                </Card>
            </div>

            <Card>
                <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div className="font-mono-label">Monthly attendance rollup</div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Select value={rollupMid} onValueChange={setRollupMid}>
                            <SelectTrigger className="w-56" data-testid="rollup-member"><SelectValue placeholder="Pick a member" /></SelectTrigger>
                            <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="month" value={rollupMonth} onChange={(e) => setRollupMonth(e.target.value)} className="w-40" data-testid="rollup-month" />
                    </div>
                </div>
                {rollup ? (
                    <>
                        <div className="grid grid-cols-3 gap-3 mt-5">
                            <div className="rounded-xl border border-gold/40 p-3"><div className="font-mono-label text-gold">Present</div><div className="font-display text-2xl text-gold">{rollup.counts.Present || 0}</div></div>
                            <div className="rounded-xl border border-sand/40 p-3"><div className="font-mono-label" style={{color:"#C8BFB0"}}>Late</div><div className="font-display text-2xl text-sand">{rollup.counts.Late || 0}</div></div>
                            <div className="rounded-xl border border-crimson/40 p-3"><div className="font-mono-label text-crimson">Absent</div><div className="font-display text-2xl text-crimson">{rollup.counts.Absent || 0}</div></div>
                        </div>
                        <div className="grid grid-cols-7 gap-2 mt-6" data-testid="rollup-calendar">
                            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={`h${i}`} className="font-mono-label text-center text-sand/70">{d}</div>)}
                            {Array.from({ length: startDow(rollupMonth) }).map((_, i) => <div key={`pad${i}`} />)}
                            {Array.from({ length: daysInMonth(rollupMonth) }).map((_, i) => {
                                const day = i + 1;
                                const dateStr = `${rollupMonth}-${String(day).padStart(2, "0")}`;
                                const s = statusOf(dateStr);
                                return (
                                    <div
                                        key={dateStr}
                                        title={s ? `${dateStr} · ${s}` : dateStr}
                                        className={`aspect-square rounded-lg border grid place-items-center text-sm ${cellColor(s)}`}
                                    >
                                        {day}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className="text-sand/70 mt-6 text-sm">Pick a member to see her month at a glance.</div>
                )}
            </Card>
        </div>
    );
};

// ---------- Expense ----------
const EXPENSE_CATEGORIES = ["Utilities", "Maintenance", "Bills", "Equipment", "Salaries", "Marketing", "Other"];
const PAYMENT_MODES = [
    { k: "cash", l: "Cash" }, { k: "upi", l: "UPI" },
    { k: "card", l: "Card" }, { k: "cheque", l: "Cheque" },
];
const ExpenseTab = () => {
    const [rows, setRows] = useState([]);
    const [subcats, setSubcats] = useState({});
    const [f, setF] = useState({
        date: todayStr(), category: "Utilities", subcategory: "",
        amount: "", payment_mode: "cash", note: "",
    });
    const load = useCallback(async () => {
        const [list, subs] = await Promise.all([
            api.get("/admin/expenses"),
            api.get("/admin/expense-subcategories").catch(() => ({ data: {} })),
        ]);
        setRows(list.data); setSubcats(subs.data || {});
    }, []);
    useEffect(() => { load(); }, [load]);
    const currentSubs = useMemo(() => subcats[f.category] || [], [subcats, f.category]);
    useEffect(() => {
        // reset subcategory if category changes and current value no longer valid
        if (f.subcategory && !currentSubs.includes(f.subcategory)) {
            setF((p) => ({ ...p, subcategory: "" }));
        }
    }, [f.category, currentSubs, f.subcategory]);
    const add = async () => {
        if (!f.amount) return toast.error("Amount required");
        try {
            await api.post("/admin/expenses", { ...f, amount: Number(f.amount) });
            toast.success("Expense added"); load();
            setF({ ...f, amount: "", note: "", subcategory: "" });
        } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    };
    const del = async (id) => { await api.delete(`/admin/expenses/${id}`); load(); };
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <Card>
                <div className="font-mono-label">Add expense</div>
                <Label className="mt-4 block">Date</Label>
                <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="exp-date" />
                <Label className="mt-3 block">Category</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                    <SelectTrigger data-testid="exp-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Label className="mt-3 block">Subcategory</Label>
                <Select value={f.subcategory || "__none__"}
                        onValueChange={(v) => setF({ ...f, subcategory: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="exp-subcat"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {currentSubs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Label className="mt-3 block">Amount (₹)</Label>
                <Input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="exp-amount" />
                <Label className="mt-3 block">Payment mode</Label>
                <Select value={f.payment_mode} onValueChange={(v) => setF({ ...f, payment_mode: v })}>
                    <SelectTrigger data-testid="exp-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.k} value={m.k}>{m.l}</SelectItem>)}</SelectContent>
                </Select>
                <Label className="mt-3 block">Note</Label>
                <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="exp-note" />
                <Button onClick={add} className="btn-gold w-full mt-5" data-testid="exp-save">Add</Button>
            </Card>
            <Card className="lg:col-span-2">
                <div className="font-mono-label">All expenses</div>
                <Table className="mt-3"><TableHeader><TableRow>
                    <TableHead>Date</TableHead><TableHead>Category</TableHead>
                    <TableHead>Subcategory</TableHead><TableHead>Mode</TableHead>
                    <TableHead>Amount</TableHead><TableHead>Note</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                    <TableBody>{rows.map((r) => (
                        <TableRow key={r.id} data-testid={`exp-row-${r.id}`}>
                            <TableCell>{r.date}</TableCell>
                            <TableCell className="text-gold">{r.category}</TableCell>
                            <TableCell className="text-sand text-xs">{r.subcategory || "—"}</TableCell>
                            <TableCell className="text-sand text-xs uppercase">{r.payment_mode || "—"}</TableCell>
                            <TableCell className="text-ivory">{money(r.amount)}</TableCell>
                            <TableCell className="text-sand/80 text-xs">{r.note}</TableCell>
                            <TableCell><button onClick={() => del(r.id)} className="text-crimson text-xs" data-testid={`exp-del-${r.id}`}>Delete</button></TableCell>
                        </TableRow>
                    ))}{!rows.length && <TableRow><TableCell colSpan={7} className="text-sand/70 text-center py-6">No expenses yet.</TableCell></TableRow>}</TableBody>
                </Table>
            </Card>
        </div>
    );
};

// ---------- Billing ----------
const minPastDate = () => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
};
const BillingTab = ({ isAdmin }) => {
    const [rows, setRows] = useState([]); const [members, setMembers] = useState([]);
    const [f, setF] = useState({
        member_id: "", date: todayStr(), amount: "", discount: 0,
        gst_enabled: true, gst_pct: 18, payment_mode: "cash", coupon_code: "",
        due_date: "", follow_up_date: "",
    });
    const [payFor, setPayFor] = useState(null);
    const [payAmt, setPayAmt] = useState("");
    const [payMode, setPayMode] = useState("cash");
    const [showFollowups, setShowFollowups] = useState(false);
    const [followups, setFollowups] = useState([]);
    const load = useCallback(async () => {
        const [a, b] = await Promise.all([api.get("/admin/invoices"), api.get("/admin/members")]);
        setRows(a.data); setMembers(b.data);
        if (showFollowups) setFollowups((await api.get("/admin/invoices/followups")).data);
    }, [showFollowups]);
    const resend = async (id) => {
        try {
            const { data } = await api.post(`/admin/invoices/${id}/resend`);
            toast.success(data.email_sent ? `Invoice re-sent to ${data.email}` : "Invoice queued (no email on member)");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed");
        }
    };
    useEffect(() => { load(); }, [load]);
    const preview = useMemo(() => {
        const amount = Number(f.amount) || 0;
        const discount = Number(f.discount) || 0;
        const base = Math.max(0, amount - discount);
        const gst = f.gst_enabled ? +(base * f.gst_pct / 100).toFixed(2) : 0;
        const total = +(base + gst).toFixed(2);
        return { amount, discount, base, gst, total };
    }, [f.amount, f.discount, f.gst_enabled, f.gst_pct]);
    const add = async () => {
        const m = members.find((x) => x.id === f.member_id);
        if (!m) return toast.error("Pick a member");
        if (!f.amount) return toast.error("Amount required");
        if (f.date < minPastDate()) return toast.error("Cannot back-date beyond 30 days");
        if (f.date > todayStr()) return toast.error("Invoice date cannot be in the future");
        const invoice_no = `AAE-${new Date().getFullYear()}-${(rows.length + 3).toString().padStart(4, "0")}`;
        try {
            await api.post("/admin/invoices", {
                invoice_no, member_id: m.id, member_name: m.name, date: f.date,
                amount: preview.amount, discount: preview.discount, gst: preview.gst, total: preview.total,
                payment_mode: f.payment_mode, coupon_code: f.coupon_code || "",
                due_date: f.due_date || null, follow_up_date: f.follow_up_date || null,
            });
            toast.success("Invoice generated · email sent"); load();
            setF({ ...f, amount: "", discount: 0, coupon_code: "", due_date: "", follow_up_date: "" });
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not create invoice"); }
    };
    const mark = async (id, status) => { await api.patch(`/admin/invoices/${id}`, { status }); load(); };
    const savePayment = async () => {
        if (!payFor || !payAmt) return;
        try {
            const { data } = await api.post(`/admin/invoices/${payFor.id}/payment`,
                { amount: Number(payAmt), method: payMode });
            toast.success(`Recorded · balance ${money(data.balance)}${data.email_sent ? " · email sent" : ""}`);
            setPayFor(null); setPayAmt(""); setPayMode("cash"); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };
    const displayed = showFollowups ? followups : rows;
    const statusColor = (s) => s === "Paid" ? "text-gold" : s === "Overdue" ? "text-crimson" : s === "Partial" ? "text-emerald-400" : "text-sand";
    return (
        <div className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
                <Card>
                    <div className="font-mono-label">New invoice</div>
                    <Label className="mt-4 block">Member</Label>
                    <Select value={f.member_id} onValueChange={(v) => setF({ ...f, member_id: v })}>
                        <SelectTrigger data-testid="inv-member"><SelectValue placeholder="Choose" /></SelectTrigger>
                        <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Label className="mt-3 block">Invoice date</Label>
                    <Input type="date" value={f.date} min={minPastDate()} max={todayStr()}
                           onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="inv-date" />
                    <div className="text-[10px] text-sand/60 mt-1">Back-dating allowed up to 30 days.</div>
                    <Label className="mt-3 block">Amount (₹)</Label>
                    <Input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="inv-amount" />
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                            <Label className="block">Discount (₹)</Label>
                            <Input value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })}
                                   placeholder="0" data-testid="inv-discount" />
                        </div>
                        <div>
                            <Label className="block">Coupon</Label>
                            <Input value={f.coupon_code} onChange={(e) => setF({ ...f, coupon_code: e.target.value.toUpperCase() })}
                                   placeholder="e.g. EVE20" data-testid="inv-coupon" />
                            <CouponValidator code={f.coupon_code} amount={f.amount} memberId={f.member_id} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                        <Label>GST on this invoice</Label>
                        <button type="button" onClick={() => setF({ ...f, gst_enabled: !f.gst_enabled })}
                                className={`text-xs px-3 py-1 rounded-full border transition-colors ${f.gst_enabled ? "border-gold text-gold bg-gold/10" : "border-sand/30 text-sand/60"}`}
                                data-testid="inv-gst-toggle">
                            {f.gst_enabled ? "ON" : "OFF"}
                        </button>
                    </div>
                    {f.gst_enabled && (
                        <>
                            <Label className="mt-3 block">GST %</Label>
                            <Input value={f.gst_pct} onChange={(e) => setF({ ...f, gst_pct: Number(e.target.value || 0) })} data-testid="inv-gst-pct" />
                        </>
                    )}
                    <Label className="mt-3 block">Payment mode</Label>
                    <Select value={f.payment_mode} onValueChange={(v) => setF({ ...f, payment_mode: v })}>
                        <SelectTrigger data-testid="inv-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.k} value={m.k}>{m.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Label className="mt-3 block">Due date</Label>
                    <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="inv-due" />
                    <Label className="mt-3 block">Follow-up date</Label>
                    <Input type="date" value={f.follow_up_date} onChange={(e) => setF({ ...f, follow_up_date: e.target.value })} data-testid="inv-follow" />
                    <div className="mt-4 rounded-xl border border-gold/25 bg-gold/5 p-3 space-y-1 text-xs" data-testid="inv-preview">
                        <div className="flex justify-between text-sand/80"><span>Subtotal</span><span>{money(preview.amount)}</span></div>
                        {preview.discount > 0 && <div className="flex justify-between text-crimson"><span>Discount</span><span>−{money(preview.discount)}</span></div>}
                        <div className="flex justify-between text-sand/80"><span>GST ({f.gst_enabled ? `${f.gst_pct}%` : "off"})</span><span>{money(preview.gst)}</span></div>
                        <div className="flex justify-between pt-1 border-t border-gold/20 text-gold font-mono-label text-sm"><span>Total</span><span>{money(preview.total)}</span></div>
                    </div>
                    <Button onClick={add} className="btn-gold w-full mt-5" data-testid="inv-save">Generate & email</Button>
                </Card>
                <Card className="lg:col-span-2">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="font-mono-label">{showFollowups ? "Follow-ups needed" : "Invoices"}</div>
                        <button onClick={() => setShowFollowups((v) => !v)} className="btn-ghost-gold text-xs" data-testid="inv-toggle-followups">
                            {showFollowups ? "Show all" : "Show follow-ups"}
                        </button>
                    </div>
                    <Table className="mt-3"><TableHeader><TableRow>
                        <TableHead>No.</TableHead><TableHead>Member</TableHead><TableHead>Date</TableHead>
                        <TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Balance</TableHead>
                        <TableHead>Due</TableHead><TableHead>Mode</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{displayed.map((r) => (
                        <TableRow key={r.id} data-testid={`inv-row-${r.id}`}>
                            <TableCell className="text-gold text-xs">
                                {r.invoice_no}
                                {r.is_renewal && <span className="ml-1 inline-block text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-900/40 border border-emerald-400/40 text-emerald-300" data-testid={`inv-renewal-${r.id}`}>RENEWAL</span>}
                            </TableCell>
                            <TableCell className="text-ivory">{r.member_name}</TableCell>
                            <TableCell className="text-sand text-xs">{r.date}</TableCell>
                            <TableCell>
                                {money(r.total)}
                                {r.discount > 0 && <div className="text-[10px] text-crimson/80">−{money(r.discount)} disc</div>}
                            </TableCell>
                            <TableCell className="text-emerald-400">{money(r.paid_amount || 0)}</TableCell>
                            <TableCell className={r.balance > 0 ? "text-crimson" : "text-sand"}>{money(r.balance ?? (r.total - (r.paid_amount || 0)))}</TableCell>
                            <TableCell className={r.is_overdue ? "text-crimson text-xs" : "text-sand/80 text-xs"}>{r.due_date || "—"}</TableCell>
                            <TableCell className="text-sand/80 text-xs uppercase">{r.payment_mode || "—"}</TableCell>
                            <TableCell className={`${statusColor(r.status)} text-sm`}>{r.status}</TableCell>
                            <TableCell className="flex gap-3 flex-wrap">
                                {(r.balance ?? (r.total - (r.paid_amount || 0))) > 0 && (
                                    <button onClick={() => { setPayFor(r); setPayAmt(""); setPayMode(r.payment_mode || "cash"); }} className="text-gold text-xs" data-testid={`inv-pay-${r.id}`}>Record payment</button>
                                )}
                                {r.download_token && (
                                    <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/invoices/download/${r.download_token}`}
                                       target="_blank" rel="noreferrer"
                                       className="text-sand text-xs hover:text-gold"
                                       data-testid={`inv-pdf-${r.id}`}>PDF</a>
                                )}
                                <button onClick={() => resend(r.id)} className="text-emerald-400 text-xs hover:underline"
                                        data-testid={`inv-resend-${r.id}`}>Resend</button>
                                <Select value={r.status} onValueChange={(v) => mark(r.id, v)}>
                                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                                    <SelectContent>{["Paid", "Pending", "Partial", "Overdue"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </TableCell>
                        </TableRow>
                    ))}{!displayed.length && <TableRow><TableCell colSpan={10} className="text-sand/70 text-center py-6">
                        {showFollowups ? "No follow-ups today — everyone's current." : "No invoices yet."}
                    </TableCell></TableRow>}</TableBody></Table>
                </Card>
            </div>

            {payFor && (
                <div className="fixed inset-0 bg-obsidian/80 backdrop-blur z-50 grid place-items-center p-4" data-testid="pay-modal">
                    <Card className="max-w-md w-full">
                        <div className="font-mono-label">Record payment</div>
                        <div className="text-ivory font-display text-xl mt-2">{payFor.invoice_no} · {payFor.member_name}</div>
                        <div className="text-sand text-sm mt-1">Total {money(payFor.total)} · Paid {money(payFor.paid_amount || 0)} · Balance {money(payFor.total - (payFor.paid_amount || 0))}</div>
                        <Label className="mt-4 block">Amount received (₹)</Label>
                        <Input value={payAmt} onChange={(e) => setPayAmt(e.target.value)} placeholder="e.g. 4000" data-testid="pay-amount" />
                        <Label className="mt-3 block">Payment mode</Label>
                        <Select value={payMode} onValueChange={setPayMode}>
                            <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.k} value={m.k}>{m.l}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="flex gap-3 mt-4">
                            <Button onClick={savePayment} className="btn-gold flex-1" data-testid="pay-confirm">Record</Button>
                            <Button variant="ghost" onClick={() => setPayFor(null)} className="text-sand">Cancel</Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

// ---------- Collections (admin only) ----------
const CollectionsTab = () => {
    const [range, setRange] = useState("month");
    const [start, setStart] = useState(todayStr());
    const [end, setEnd] = useState(todayStr());
    const [data, setData] = useState(null);
    const load = useCallback(async () => {
        const params = range === "custom" ? { range, start, end } : { range };
        const { data } = await api.get("/admin/collections", { params });
        setData(data);
    }, [range, start, end]);
    useEffect(() => { load(); }, [range, load]);
    return (
        <div className="space-y-6">
            <Card>
                <div className="flex items-center gap-3 flex-wrap justify-between">
                    <div className="font-mono-label">Collections dashboard</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {["month", "year", "custom"].map((r) => (
                            <button key={r} onClick={() => setRange(r)}
                                className={`text-xs px-4 py-2 rounded-full border transition-colors ${range === r ? "border-gold text-gold bg-gold/10" : "border-gold/25 text-sand hover:border-gold/60"}`}
                                data-testid={`col-range-${r}`}>{r === "month" ? "This month" : r === "year" ? "This year" : "Custom"}</button>
                        ))}
                        {range === "custom" && (
                            <>
                                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="col-start" />
                                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="col-end" />
                                <Button onClick={load} className="btn-gold" data-testid="col-apply">Apply</Button>
                            </>
                        )}
                    </div>
                </div>
            </Card>
            {data && (
                <>
                    <div className="grid lg:grid-cols-3 gap-5">
                        <Card><div className="font-mono-label">Total collected</div><div className="font-display text-4xl gold-text mt-3">{money(data.total)}</div><div className="text-xs text-sand/70 mt-1">{data.start} → {data.end}</div></Card>
                        <Card><div className="font-mono-label">Transactions</div><div className="font-display text-4xl gold-text mt-3">{data.count}</div><div className="text-xs text-sand/70 mt-1">payments recorded</div></Card>
                        <Card><div className="font-mono-label">Daily average</div><div className="font-display text-4xl gold-text mt-3">{money(data.count ? data.total / Math.max(1, data.daily.length) : 0)}</div><div className="text-xs text-sand/70 mt-1">per active day</div></Card>
                    </div>
                    <Card>
                        <div className="font-mono-label">Daily inflow</div>
                        <div className="h-72 mt-3">
                            <ResponsiveContainer><BarChart data={data.daily}>
                                <XAxis dataKey="date" stroke="#8A8275" fontSize={11} />
                                <YAxis stroke="#8A8275" fontSize={11} />
                                <Tooltip contentStyle={{ background: "#121016", border: "1px solid #D4AF37", color: "#FAF5E9" }} formatter={(v) => money(v)} />
                                <Bar dataKey="amount" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                            </BarChart></ResponsiveContainer>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
};

// ---------- Employees (with Run Payroll) ----------
const EmployeesTab = () => {
    const [rows, setRows] = useState([]);
    const [f, setF] = useState({ name: "", role: "", phone: "", salary: "", shift: "", joined: todayStr() });
    const [payrollMonth, setPayrollMonth] = useState(monthKey());
    const [busyPayroll, setBusyPayroll] = useState(false);
    const load = async () => setRows((await api.get("/admin/employees")).data);
    useEffect(() => { load(); }, []);
    const add = async () => {
        if (!f.name) return;
        await api.post("/admin/employees", { ...f, salary: Number(f.salary || 0) });
        toast.success("Employee added"); load();
        setF({ ...f, name: "", role: "", phone: "", salary: "", shift: "" });
    };
    const del = async (id) => { await api.delete(`/admin/employees/${id}`); load(); };
    const activeCount = rows.filter((r) => r.active !== false).length;
    const monthTotal = rows.filter((r) => r.active !== false).reduce((s, r) => s + (r.salary || 0), 0);
    const runPayroll = async () => {
        setBusyPayroll(true);
        try {
            const { data } = await api.post(`/admin/payroll/run?month=${payrollMonth}`);
            toast.success(`Payroll run · ${data.count} salaries · ${money(data.total)} → Expenses`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not run payroll");
        }
        setBusyPayroll(false);
    };
    return (
        <div className="space-y-6">
            <Card>
                <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div>
                        <div className="font-mono-label">Run monthly payroll</div>
                        <div className="text-sand text-sm mt-1">Creates a Salaries expense row per active employee — one-tap, idempotent per month.</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Input type="month" value={payrollMonth} onChange={(e) => setPayrollMonth(e.target.value)} className="w-40" data-testid="payroll-month" />
                        <div className="text-right hidden sm:block">
                            <div className="font-mono-label !text-[10px]">{activeCount} active · month total</div>
                            <div className="font-display text-xl gold-text">{money(monthTotal)}</div>
                        </div>
                        <Button onClick={runPayroll} disabled={busyPayroll || !activeCount} className="btn-gold" data-testid="payroll-run-btn">
                            <Play className="w-4 h-4 mr-1" /> {busyPayroll ? "Running…" : "Run payroll"}
                        </Button>
                    </div>
                </div>
            </Card>
            <div className="grid lg:grid-cols-3 gap-6">
                <Card>
                    <div className="font-mono-label">Add employee</div>
                    <div className="space-y-3 mt-4">
                        <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="emp-name" />
                        <Input placeholder="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
                        <Input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                        <Input placeholder="Salary (₹)" value={f.salary} onChange={(e) => setF({ ...f, salary: e.target.value })} />
                        <Input placeholder="Shift e.g. 6am-2pm" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} />
                        <Input type="date" value={f.joined} onChange={(e) => setF({ ...f, joined: e.target.value })} />
                        <Button onClick={add} className="btn-gold w-full" data-testid="emp-save">Add</Button>
                    </div>
                </Card>
                <Card className="lg:col-span-2">
                    <div className="font-mono-label">Roster</div>
                    <Table className="mt-3"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Shift</TableHead><TableHead>Salary</TableHead><TableHead></TableHead></TableRow></TableHeader>
                        <TableBody>{rows.map((r) => (<TableRow key={r.id}>
                            <TableCell className="text-ivory">{r.name}</TableCell><TableCell className="text-gold text-sm">{r.role}</TableCell>
                            <TableCell>{r.shift}</TableCell><TableCell>{money(r.salary)}</TableCell>
                            <TableCell><button onClick={() => del(r.id)} className="text-crimson text-xs" data-testid={`emp-del-${r.id}`}>Remove</button></TableCell>
                        </TableRow>))}</TableBody></Table>
                </Card>
            </div>
        </div>
    );
};

// ---------- Members (with prefill from Leads convert) ----------
const MembersTab = ({ prefill, clearPrefill, isAdmin }) => {
    const [rows, setRows] = useState([]);
    const [f, setF] = useState({ name: "", phone: "", email: "", plan: "Eve Gold", joined: todayStr(), biometric_id: "" });
    const [managing, setManaging] = useState(null);
    const load = async () => setRows((await api.get("/admin/members")).data);
    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (prefill) {
            setF({
                name: prefill.name || "", phone: prefill.phone || "", email: prefill.email || "",
                plan: "Eve Gold", joined: todayStr(), biometric_id: "",
            });
            toast.info("Lead prefilled — review and click Add.");
        }
    }, [prefill]);
    const add = async () => {
        if (!f.name) return;
        await api.post("/admin/members", f);
        toast.success("Member added");
        if (prefill?.leadId) {
            try { await api.patch(`/admin/leads/${prefill.leadId}`, { status: "converted" }); } catch (err) { console.warn("lead-to-member: status flip failed (non-blocking)", err); }
        }
        clearPrefill?.();
        load();
        setF({ name: "", phone: "", email: "", plan: "Eve Gold", joined: todayStr(), biometric_id: "" });
    };
    const del = async (id) => { await api.delete(`/admin/members/${id}`); load(); };
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <Card>
                <div className="font-mono-label">{prefill ? "Convert lead → member" : "Add member"}</div>
                <div className="space-y-3 mt-4">
                    <Input placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mem-name" />
                    <Input placeholder="+91 Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
                    <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                    <Select value={f.plan} onValueChange={(v) => setF({ ...f, plan: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{["Eve Gold", "Eve Diamond", "Eve Empress VIP"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
                    <Input type="date" value={f.joined} onChange={(e) => setF({ ...f, joined: e.target.value })} />
                    <Input placeholder="Biometric ID (ESSL enrollment)" value={f.biometric_id || ""}
                           onChange={(e) => setF({ ...f, biometric_id: e.target.value })} data-testid="mem-bio" />
                    <Button onClick={add} className="btn-gold w-full" data-testid="mem-save">
                        {prefill ? "Confirm & convert" : "Add"}
                    </Button>
                    {prefill && <Button variant="ghost" onClick={clearPrefill} className="w-full text-sand" data-testid="mem-cancel-prefill">Cancel prefill</Button>}
                </div>
            </Card>
            <Card className="lg:col-span-2">
                <div className="font-mono-label">Members</div>
                <Table className="mt-3"><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Plan</TableHead><TableHead>Phone</TableHead><TableHead>Joined</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>{rows.map((r) => (<TableRow key={r.id}>
                        <TableCell className="text-ivory">{r.name}</TableCell><TableCell className="text-gold">{r.plan}</TableCell>
                        <TableCell>{r.phone}</TableCell><TableCell>{r.joined}</TableCell>
                        <TableCell className="flex gap-3">
                            <button onClick={() => setManaging(r)} className="text-gold text-xs hover:underline"
                                    data-testid={`mem-manage-${r.id}`}>Manage</button>
                            <button onClick={() => del(r.id)} className="text-crimson text-xs" data-testid={`mem-del-${r.id}`}>Remove</button>
                        </TableCell>
                    </TableRow>))}</TableBody></Table>
            </Card>
            <MemberManagerModal member={managing} open={!!managing}
                                onClose={() => setManaging(null)} isAdmin={isAdmin}
                                onMemberUpdate={(upd) => { setManaging(upd); load(); }} />
        </div>
    );
};

// ---------- Leads (with Convert + archive filter) ----------
const LeadsTab = ({ onConvert }) => {
    const [rows, setRows] = useState([]);
    const [emps, setEmps] = useState([]);
    const [view, setView] = useState("active"); // active | archived | all
    const [search, setSearch] = useState("");
    const load = async () => {
        const [l, e] = await Promise.all([api.get("/admin/leads"), api.get("/admin/employees").catch(() => ({ data: [] }))]);
        setRows(l.data); setEmps(e.data);
    };
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => { await api.patch(`/admin/leads/${id}`, { status }); load(); };
    const setHandler = async (id, handler_staff_id) => {
        await api.patch(`/admin/leads/${id}`, { handler_staff_id: handler_staff_id === "__none__" ? "" : handler_staff_id });
        toast.success("Handler assigned"); load();
    };
    const del = async (id) => { await api.delete(`/admin/leads/${id}`); toast.success("Removed"); load(); };
    const badge = (s) => s === "new" ? "text-crimson" : s === "contacted" ? "text-gold" : s === "converted" ? "text-emerald-400" : s === "archived" ? "text-sand/50" : "text-sand";

    const activeCount = rows.filter((r) => r.status !== "archived").length;
    const archivedCount = rows.filter((r) => r.status === "archived").length;
    const displayed = useMemo(() => {
        const base = view === "active" ? rows.filter((r) => r.status !== "archived")
                   : view === "archived" ? rows.filter((r) => r.status === "archived")
                   : rows;
        if (!search.trim()) return base;
        const q = search.trim().toLowerCase();
        return base.filter((r) =>
            (r.name || "").toLowerCase().includes(q) ||
            (r.phone || "").includes(q) ||
            (r.email || "").toLowerCase().includes(q) ||
            (r.lead_source || "").toLowerCase().includes(q) ||
            (r.handler_staff_name || "").toLowerCase().includes(q)
        );
    }, [rows, view, search]);
    const isArchived = view === "archived";

    return (
        <Card>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono-label">Trial bookings & callbacks</div>
                    <div className="text-xs text-sand/70 mt-1">
                        {activeCount} active \u00b7 {archivedCount} archived \u00b7 showing {displayed.length}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Input value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder="Search name / phone / email…" className="w-56 text-xs"
                           data-testid="leads-search" />
                    <div className="inline-flex rounded-lg border border-gold/25 overflow-hidden" data-testid="leads-view-toggle">
                        {[["active","Active"],["archived","Archived"],["all","All"]].map(([v,l]) => (
                            <button key={v} onClick={() => setView(v)}
                                    data-testid={`leads-view-${v}`}
                                    className={`px-3 py-1 text-xs ${view===v?"bg-gold/15 text-gold":"text-sand hover:text-ivory"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <Table className="mt-4">
                <TableHeader><TableRow>
                    <TableHead>When</TableHead><TableHead>Name</TableHead><TableHead>Phone</TableHead>
                    <TableHead>Discipline</TableHead>
                    {!isArchived && <TableHead>Handler</TableHead>}
                    <TableHead>Status</TableHead>
                    {isArchived && <TableHead>Legacy</TableHead>}
                    {!isArchived && <TableHead></TableHead>}
                </TableRow></TableHeader>
                <TableBody>{displayed.map((r) => (
                    <TableRow key={r.id} data-testid={`lead-row-${r.id}`} className={isArchived ? "opacity-70" : ""}>
                        <TableCell className="text-xs text-sand/80">{new Date(r.created_at).toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-ivory">{r.name}</TableCell>
                        <TableCell><a href={`tel:${r.phone}`} className="text-gold hover:underline"><PhoneCall className="inline w-3 h-3 mr-1"/>{r.phone}</a></TableCell>
                        <TableCell className="text-sand text-sm">{r.discipline || "—"}</TableCell>
                        {!isArchived && (
                            <TableCell>
                                <Select value={r.handler_staff_id || "__none__"}
                                        onValueChange={(v) => setHandler(r.id, v)}>
                                    <SelectTrigger className="w-40 text-xs" data-testid={`lead-handler-${r.id}`}>
                                        <SelectValue placeholder="Assign…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— Unassigned —</SelectItem>
                                        {emps.filter((e) => e.active !== false).map((e) =>
                                            <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </TableCell>
                        )}
                        <TableCell className={badge(r.status) + " text-xs uppercase"}>
                            {isArchived ? "ARCHIVED" : (
                                <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                                    <SelectTrigger className={`w-32 ${badge(r.status)}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>{["new", "contacted", "converted", "lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            )}
                        </TableCell>
                        {isArchived && (
                            <TableCell className="text-xs text-sand/60">
                                {r.legacy_status || "—"}{r.handler_staff_name ? ` \u00b7 ${r.handler_staff_name}` : ""}
                            </TableCell>
                        )}
                        {!isArchived && (
                            <TableCell className="flex gap-3">
                                <button
                                    onClick={() => onConvert({ name: r.name, phone: r.phone, email: r.email, leadId: r.id })}
                                    className="text-gold text-xs hover:underline"
                                    data-testid={`lead-convert-${r.id}`}
                                    disabled={r.status === "converted"}
                                >
                                    <ArrowRightLeft className="inline w-3 h-3 mr-1" /> Convert
                                </button>
                                <button onClick={() => del(r.id)} className="text-crimson text-xs" data-testid={`lead-del-${r.id}`}>Remove</button>
                            </TableCell>
                        )}
                    </TableRow>
                ))}{!displayed.length && <TableRow><TableCell colSpan={isArchived ? 6 : 7} className="text-sand/70 text-center py-6">
                    {search ? `No leads match "${search}".` : (isArchived ? "No archived enquiries." : "No active leads yet — bookings from the landing page will land here.")}
                </TableCell></TableRow>}</TableBody>
            </Table>
        </Card>
    );
};

// ---------- Codex ----------
const CodexTab = () => {
    const [rows, setRows] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const load = async () => setRows((await api.get("/admin/codex")).data);
    useEffect(() => { load(); }, []);

    const chartData = useMemo(() => {
        const paid = rows.filter((r) => r.status === "paid");
        const byPlan = paid.reduce((a, r) => { a[r.plan] = (a[r.plan] || 0) + r.amount; return a; }, {});
        return ["lifetime", "monthly"].map((p) => ({
            plan: p === "lifetime" ? "Lifetime" : "Monthly",
            revenue: (byPlan[p] || 0) / 100,
            count: paid.filter((r) => r.plan === p).length,
        }));
    }, [rows]);
    const mrr = chartData.find((d) => d.plan === "Monthly")?.revenue || 0;
    const ltv = chartData.find((d) => d.plan === "Lifetime")?.revenue || 0;
    const totalPaid = rows.filter((r) => r.status === "paid").length;

    const refund = async (id) => {
        if (!window.confirm("Issue a full refund via Razorpay? This can't be undone.")) return;
        setBusyId(id);
        try {
            await api.post(`/admin/codex/${id}/refund`);
            toast.success("Refund initiated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Refund failed");
        }
        setBusyId(null);
    };
    const resend = async (id) => {
        setBusyId(id);
        try {
            const { data } = await api.post(`/admin/codex/${id}/resend`);
            toast.success(data.email_sent ? `Codex re-sent to ${data.email}` : `Queued — no email delivered (check backend logs)`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed");
        }
        setBusyId(null);
    };

    const statusColor = (s) => s === "paid" ? "text-emerald-400" : s === "refunded" ? "text-sand" : s === "signature_failed" ? "text-crimson" : "text-gold";

    return (
        <div className="space-y-6">
            <div className="grid lg:grid-cols-4 gap-5">
                <Card><div className="font-mono-label">Paid purchases</div><div className="font-display text-3xl gold-text mt-3">{totalPaid}</div><div className="text-xs text-sand/70 mt-1">Total sold</div></Card>
                <Card><div className="font-mono-label">Lifetime revenue</div><div className="font-display text-3xl gold-text mt-3">{money(ltv)}</div><div className="text-xs text-sand/70 mt-1">One-time ₹899</div></Card>
                <Card><div className="font-mono-label">Monthly revenue (MRR)</div><div className="font-display text-3xl gold-text mt-3">{money(mrr)}</div><div className="text-xs text-sand/70 mt-1">Recurring ₹99</div></Card>
                <Card><div className="font-mono-label">Total collected</div><div className="font-display text-3xl gold-text mt-3">{money(mrr + ltv)}</div><div className="text-xs text-sand/70 mt-1">Since launch</div></Card>
            </div>

            <Card>
                <div className="font-mono-label">Revenue by plan</div>
                <div className="h-64 mt-3">
                    <ResponsiveContainer><BarChart data={chartData}>
                        <XAxis dataKey="plan" stroke="#8A8275" fontSize={12} />
                        <YAxis stroke="#8A8275" fontSize={12} />
                        <Tooltip contentStyle={{ background: "#121016", border: "1px solid #D4AF37", color: "#FAF5E9" }} formatter={(v) => money(v)} />
                        <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
                            {chartData.map((d) => <Cell key={d.plan} fill={d.plan === "Lifetime" ? "#D4AF37" : "#DC143C"} />)}
                        </Bar>
                    </BarChart></ResponsiveContainer>
                </div>
            </Card>

            <Card>
                <div className="font-mono-label">Purchases</div>
                <Table className="mt-3"><TableHeader><TableRow>
                    <TableHead>When</TableHead><TableHead>Buyer</TableHead><TableHead>Plan</TableHead>
                    <TableHead>Amount</TableHead><TableHead>Payment ID</TableHead>
                    <TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>{rows.map((r) => (
                    <TableRow key={r.id} data-testid={`codex-row-${r.id}`}>
                        <TableCell className="text-xs text-sand/80">{new Date(r.created_at).toLocaleString("en-IN")}</TableCell>
                        <TableCell>
                            <div className="text-ivory">{r.name}</div>
                            <div className="text-xs text-sand/70">{r.email}</div>
                        </TableCell>
                        <TableCell className="text-gold text-sm capitalize">{r.plan}</TableCell>
                        <TableCell className="text-ivory">{money((r.amount || 0) / 100)}</TableCell>
                        <TableCell className="font-mono text-xs text-sand/80">{r.payment_id || "—"}</TableCell>
                        <TableCell className={`${statusColor(r.status)} capitalize text-sm`}>{r.status?.replace("_", " ")}</TableCell>
                        <TableCell className="flex flex-wrap gap-3">
                            <button
                                onClick={() => resend(r.id)}
                                disabled={busyId === r.id || r.status !== "paid"}
                                className="text-gold text-xs hover:underline disabled:opacity-40"
                                data-testid={`codex-resend-${r.id}`}
                            >
                                <Mail className="inline w-3 h-3 mr-1" /> Resend
                            </button>
                            <button
                                onClick={() => refund(r.id)}
                                disabled={busyId === r.id || r.status !== "paid"}
                                className="text-crimson text-xs hover:underline disabled:opacity-40"
                                data-testid={`codex-refund-${r.id}`}
                            >
                                <RefreshCcw className="inline w-3 h-3 mr-1" /> Refund
                            </button>
                        </TableCell>
                    </TableRow>
                ))}{!rows.length && <TableRow><TableCell colSpan={7} className="text-sand/70 text-center py-6">No Codex purchases yet.</TableCell></TableRow>}</TableBody>
                </Table>
            </Card>
        </div>
    );
};

// ---------- Exports (CSV downloads) ----------
const ExportsTab = () => {
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const now = new Date();
    const currentFY = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    const monthKeyNow = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const [fy, setFy] = useState(currentFY);
    const [month, setMonth] = useState(monthKeyNow);
    const fyLabel = `FY ${fy}–${(fy + 1).toString().slice(-2)} (Apr ${fy} → Mar ${fy + 1})`;
    const dl = (path, label) => (
        <a href={`${API}/api/admin/export/${path}`} className="btn-gold text-xs inline-flex items-center gap-1"
           data-testid={`export-${label}`}>
            <Download className="w-3 h-3" /> {label}
        </a>
    );
    return (
        <div className="space-y-6">
            <Card>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-mono-label">Reporting window</div>
                        <div className="text-xs text-sand/70 mt-1">Choose a financial year (Indian FY: Apr → Mar) or a specific month. Entity exports (members, memberships) ignore date filters.</div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div>
                            <Label className="text-[10px] text-sand/70 block">FY starts</Label>
                            <Select value={String(fy)} onValueChange={(v) => setFy(parseInt(v, 10))}>
                                <SelectTrigger className="w-32" data-testid="export-fy"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[currentFY - 2, currentFY - 1, currentFY, currentFY + 1].map((y) => (
                                        <SelectItem key={y} value={String(y)}>FY {y}-{(y + 1).toString().slice(-2)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] text-sand/70 block">Month</Label>
                            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="export-month" />
                        </div>
                    </div>
                </div>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                    <div className="font-mono-label">All-time entity exports</div>
                    <div className="text-xs text-sand/70 mt-1">Full roster snapshots — not filtered by date.</div>
                    <div className="flex flex-wrap gap-3 mt-5">
                        {dl("members.csv", "members")}
                        {dl("memberships.csv", "memberships")}
                    </div>
                </Card>

                <Card>
                    <div className="font-mono-label">{fyLabel}</div>
                    <div className="text-xs text-sand/70 mt-1">Fiscal-year scoped expenses, invoices, payments & month-by-month turnover.</div>
                    <div className="flex flex-wrap gap-3 mt-5">
                        {dl(`expenses.csv?fy=${fy}`, "expenses-fy")}
                        {dl(`invoices.csv?fy=${fy}`, "invoices-fy")}
                        {dl(`payments.csv?fy=${fy}`, "payments-fy")}
                        {dl(`turnover.csv?fy=${fy}`, "turnover-fy")}
                    </div>
                </Card>

                <Card className="lg:col-span-2">
                    <div className="font-mono-label">Month: {month}</div>
                    <div className="text-xs text-sand/70 mt-1">Narrow exports to a specific month for closing books, GST filing, or reconciliation.</div>
                    <div className="flex flex-wrap gap-3 mt-5">
                        {dl(`expenses.csv?month=${month}`, "expenses-month")}
                        {dl(`invoices.csv?month=${month}`, "invoices-month")}
                        {dl(`payments.csv?month=${month}`, "payments-month")}
                    </div>
                </Card>
            </div>
        </div>
    );
};

// ---------- Sidebar nav ----------
const NAV_GROUPS = [
    { title: "Daily", items: [
        { v: "overview",   l: "Overview",       ic: LayoutDashboard, adminOnly: true },
        { v: "members",    l: "Members",        ic: Users },
        { v: "attendance", l: "Attendance",     ic: Sparkles },
        { v: "biometric",  l: "Biometric",      ic: Fingerprint },
        { v: "leads",      l: "Leads",          ic: MessageSquare },
    ]},
    { title: "Billing", items: [
        { v: "billing",     l: "Invoices",      ic: Receipt },
        { v: "expenses",    l: "Expenses",      ic: Wallet,     adminOnly: true },
        { v: "collections", l: "Collections",   ic: TrendingUp, adminOnly: true },
        { v: "coupons",     l: "Coupons",       ic: Ticket,     adminOnly: true },
        { v: "codex",       l: "Codex sales",   ic: ShoppingBag, adminOnly: true },
    ]},
    { title: "Memberships", items: [
        { v: "plans",     l: "Plans",           ic: CreditCard },
        { v: "followups", l: "Follow-ups",      ic: ClipboardList },
        { v: "roster",    l: "Trainer Roster",  ic: UsersRound },
        { v: "holidays",  l: "Holidays",        ic: Calendar },
    ]},
    { title: "Team & Data", items: [
        { v: "employees", l: "Employees",       ic: UserCog,  adminOnly: true },
        { v: "exports",   l: "CSV Exports",     ic: Download, adminOnly: true },
        { v: "otp",       l: "OTP Audit",       ic: ShieldCheck, adminOnly: true },
    ]},
];

const SidebarNav = ({ tab, setTab, isAdmin, onPick }) => (
    <nav className="space-y-6" data-testid="admin-sidebar-nav">
        {NAV_GROUPS.map((g) => {
            const items = g.items.filter((it) => !it.adminOnly || isAdmin);
            if (!items.length) return null;
            return (
                <div key={g.title}>
                    <div className="font-mono-label !text-[10px] text-sand/60 px-3 mb-2">{g.title}</div>
                    <div className="space-y-1">
                        {items.map((it) => {
                            const active = tab === it.v;
                            const Icon = it.ic;
                            return (
                                <button
                                    key={it.v}
                                    onClick={() => { setTab(it.v); onPick?.(); }}
                                    data-testid={`atab-${it.v}`}
                                    className={[
                                        "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                                        active
                                            ? "bg-gold/10 text-gold border border-gold/40"
                                            : "text-sand hover:bg-gold/5 hover:text-ivory border border-transparent",
                                    ].join(" ")}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{it.l}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        })}
    </nav>
);

// ---------- Root ----------
export default function Admin() {
    const { user, logout } = useAuth();
    const isAdmin = user?.role === "admin";
    const [tab, setTab] = useState(isAdmin ? "overview" : "members");
    const [prefill, setPrefill] = useState(null);
    const [navOpen, setNavOpen] = useState(false);
    const handleConvert = (data) => { setPrefill(data); setTab("members"); };
    const currentLabel = useMemo(() => {
        for (const g of NAV_GROUPS) {
            const hit = g.items.find((it) => it.v === tab);
            if (hit) return hit.l;
        }
        return "Admin";
    }, [tab]);
    return (
        <div className="min-h-screen bg-obsidian text-ivory">
            <Toaster theme="dark" richColors />
            <header className="section-x py-4 flex items-center justify-between border-b border-gold/15 sticky top-0 z-40 bg-obsidian/85 backdrop-blur-xl">
                <div className="flex items-center gap-3">
                    <button onClick={() => setNavOpen(true)} className="lg:hidden text-gold" data-testid="admin-nav-open" aria-label="Open navigation">
                        <Menu className="w-5 h-5" />
                    </button>
                    <img src="https://customer-assets-agu9un31.emergentagent.net/job_eve-fitness-hub/artifacts/mpla7sb8_AAE%20Logo.webp" alt="All About Eve" className="h-12 w-auto object-contain" />
                    <div className="hidden md:block font-mono-label !text-[10px] border-l border-gold/25 pl-3">
                        {isAdmin ? "Admin · CRM console" : "Staff · CRM"}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {isAdmin && (
                        <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/admin/go-live.pdf`}
                           target="_blank" rel="noreferrer"
                           className="btn-ghost-gold text-sm hidden md:inline-flex items-center"
                           data-testid="admin-golive-pdf">
                            <Download className="inline w-4 h-4 mr-1"/> Go-Live PDF
                        </a>
                    )}
                    <Link to="/dashboard" className="btn-ghost-gold text-sm" data-testid="admin-member-link"><Home className="inline w-4 h-4 mr-1"/> Member App</Link>
                    <span className="text-sm text-sand hidden md:block">{user?.name} · <span className="text-gold">{user?.role}</span></span>
                    <Button variant="ghost" onClick={logout} className="text-crimson" data-testid="admin-logout"><LogOut className="w-4 h-4"/></Button>
                </div>
            </header>

            {/* Mobile drawer */}
            {navOpen && (
                <div className="lg:hidden fixed inset-0 z-50 bg-obsidian/85 backdrop-blur" onClick={() => setNavOpen(false)} data-testid="admin-nav-drawer">
                    <aside className="h-full w-72 bg-obsidian-light border-r border-gold/20 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <div className="font-mono-label text-gold">Navigation</div>
                            <button onClick={() => setNavOpen(false)} data-testid="admin-nav-close" aria-label="Close navigation">
                                <X className="w-5 h-5 text-sand" />
                            </button>
                        </div>
                        <SidebarNav tab={tab} setTab={setTab} isAdmin={isAdmin} onPick={() => setNavOpen(false)} />
                    </aside>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <div className="section-x py-8 flex gap-8">
                    <aside className="hidden lg:block w-60 shrink-0 sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
                        <SidebarNav tab={tab} setTab={setTab} isAdmin={isAdmin} />
                    </aside>
                    <main className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between mb-6">
                            <h1 className="font-display text-3xl gold-text" data-testid="admin-current-tab-title">{currentLabel}</h1>
                            <div className="font-mono-label !text-[10px] text-sand/60">All About Eve · Hyderabad</div>
                        </div>
                    {isAdmin && <TabsContent value="overview"><Overview /></TabsContent>}
                    <TabsContent value="members"><MembersTab prefill={prefill} clearPrefill={() => setPrefill(null)} isAdmin={isAdmin} /></TabsContent>
                    <TabsContent value="attendance"><AttendanceTab /></TabsContent>
                    {isAdmin && <TabsContent value="expenses"><ExpenseTab /></TabsContent>}
                    <TabsContent value="billing"><BillingTab isAdmin={isAdmin} /></TabsContent>
                    {isAdmin && <TabsContent value="employees"><EmployeesTab /></TabsContent>}
                    <TabsContent value="leads"><LeadsTab onConvert={handleConvert} /></TabsContent>
                    {isAdmin && <TabsContent value="codex"><CodexTab /></TabsContent>}
                    {isAdmin && <TabsContent value="collections"><CollectionsTab /></TabsContent>}
                    <TabsContent value="plans"><PlansTab isAdmin={isAdmin} /></TabsContent>
                    <TabsContent value="followups"><FollowupsTab /></TabsContent>
                    <TabsContent value="roster"><TrainerRosterTab /></TabsContent>
                    {isAdmin && <TabsContent value="exports"><ExportsTab /></TabsContent>}
                    <TabsContent value="biometric"><BiometricTab isAdmin={isAdmin} /></TabsContent>
                    <TabsContent value="holidays"><HolidaysTab isAdmin={isAdmin} /></TabsContent>
                    {isAdmin && <TabsContent value="coupons"><CouponsTab isAdmin={isAdmin} /></TabsContent>}
                    {isAdmin && <TabsContent value="otp"><OtpAuditTab /></TabsContent>}
                    </main>
                </div>
            </Tabs>
        </div>
    );
}
